import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { useFormPersist } from '../../lib/useFormPersist';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import FileUpload from '../../components/FileUpload';
import { ArrowLeft, ArrowRight, Plus, Trash2, Check, Eye, EyeOff, Leaf, Link2, Package } from 'lucide-react';
import { toast } from 'sonner';

const INIT_PROFILE = {
  name: '', farm_name: '', location: '', story: '',
  profile_image: '', cover_image: '', gallery: [],
  farming_practices: [''], lab_reports: [], intro_video: '', internal_notes: '',
  farmer_phone: '', farmer_whatsapp: '', farmer_email: '',
};

export default function AdminAddFarmer() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [farmerId, setFarmerId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [profile, setProfile, clearProfile] = useFormPersist('add_farmer_profile_v2', INIT_PROFILE);

  // Step 2: Link to existing products
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  // Map of product_id -> { selected, procurement_price, expected_harvest_date, approximate_stock_kg }
  const [links, setLinks] = useState({});

  useEffect(() => {
    if (step === 2 && products.length === 0) {
      setLoadingProducts(true);
      api.get('/admin/products')
        .then(r => setProducts(r.data || []))
        .catch(() => toast.error('Failed to load products'))
        .finally(() => setLoadingProducts(false));
    }
  }, [step]); // eslint-disable-line

  const handleSaveProfile = async () => {
    if (!profile.name || !profile.farm_name || !profile.location) {
      toast.error('Name, Farm Name, and Location are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...profile,
        farming_practices: profile.farming_practices.filter(p => p.trim()),
        gallery: profile.gallery.filter(Boolean),
      };
      const res = await api.post('/admin/farmers', payload);
      setFarmerId(res.data.farmer_id);
      toast.success('Farmer profile saved!');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const toggleLink = (productId) => {
    setLinks(prev => {
      const next = { ...prev };
      if (next[productId]?.selected) {
        next[productId] = { ...next[productId], selected: false };
      } else {
        next[productId] = {
          selected: true,
          procurement_price: next[productId]?.procurement_price || '',
          expected_harvest_date: next[productId]?.expected_harvest_date || '',
          approximate_stock_kg: next[productId]?.approximate_stock_kg || '',
        };
      }
      return next;
    });
  };

  const updateLinkField = (productId, field, value) => {
    setLinks(prev => ({
      ...prev,
      [productId]: { ...(prev[productId] || { selected: true }), [field]: value },
    }));
  };

  const handleSaveLinks = async () => {
    const selected = Object.entries(links).filter(([, v]) => v.selected);
    if (selected.length === 0) {
      toast.error('Select at least one product to link, or skip to Review.');
      return;
    }
    setSaving(true);
    try {
      for (const [productId, v] of selected) {
        await api.post('/admin/farmer-products', {
          farmer_id: farmerId,
          product_id: productId,
          procurement_price: parseFloat(v.procurement_price) || 0,
          expected_harvest_date: v.expected_harvest_date || '',
          approximate_stock_kg: parseFloat(v.approximate_stock_kg) || 0,
          is_active: true,
        });
      }
      toast.success(`${selected.length} product(s) linked to farmer`);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link products');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/farmers/${farmerId}`, { status: 'published' });
      toast.success('Farmer published!');
      clearProfile();
      setLinks({});
      navigate('/admin/farmers');
    } catch {
      toast.error('Failed to publish');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = () => {
    toast.success('Saved as draft');
    clearProfile();
    setLinks({});
    navigate('/admin/farmers');
  };

  const filteredProducts = products.filter(p =>
    !productSearch || (p.name || '').toLowerCase().includes(productSearch.toLowerCase())
  );

  const selectedCount = Object.values(links).filter(v => v.selected).length;
  const selectedProducts = products.filter(p => links[p.product_id]?.selected);

  // Live Preview Panel
  const PreviewPanel = () => (
    <div className="bg-background rounded-xl border p-4 max-h-[600px] overflow-y-auto sticky top-6">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Customer View Preview</p>
      {profile.cover_image && <img src={profile.cover_image} alt="" className="w-full h-32 object-cover rounded-xl mb-3" />}
      <div className="flex items-center gap-3 mb-3">
        {profile.profile_image && <img src={profile.profile_image} alt="" className="w-12 h-12 rounded-xl object-cover" />}
        <div>
          <h4 className="font-serif font-medium text-sm">{profile.name || 'Farmer Name'}</h4>
          <p className="text-xs text-muted-foreground">{profile.farm_name || 'Farm Name'} {profile.location && `· ${profile.location}`}</p>
        </div>
      </div>
      {profile.farming_practices?.filter(p => p.trim()).length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">{profile.farming_practices.filter(p => p.trim()).map((p, i) => <Badge key={i} variant="secondary" className="text-xs rounded-full"><Leaf className="h-2.5 w-2.5 mr-1" />{p}</Badge>)}</div>
      )}
      {profile.story && <p className="text-xs text-muted-foreground line-clamp-4">{profile.story}</p>}
      {profile.intro_video && <video src={profile.intro_video} controls className="w-full rounded-lg mt-2 max-h-32" />}
    </div>
  );

  return (
    <div data-testid="admin-add-farmer-page" className="p-6 lg:p-8">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full"><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-serif font-medium">Add New Farmer</h1>
          <p className="text-sm text-muted-foreground">Step {step} of 3 {step === 2 && '· Link to existing products'}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)} className="rounded-full gap-2">
          {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} Preview
        </Button>
        <Button variant="ghost" size="sm" onClick={() => { clearProfile(); setLinks({}); setStep(1); setFarmerId(null); }} className="rounded-full text-xs text-muted-foreground">Clear Form</Button>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">{[1,2,3].map(s => (<div key={s} className="flex items-center gap-2 flex-1"><div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>{step > s ? <Check className="h-4 w-4" /> : s}</div>{s < 3 && <div className={`flex-1 h-0.5 ${step > s ? 'bg-primary' : 'bg-muted'}`} />}</div>))}</div>

      <div className={`grid ${showPreview ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'} gap-6 max-w-6xl`}>
        <div>
          {/* Step 1: Profile + Contact */}
          {step === 1 && (
            <Card className="rounded-xl">
              <CardHeader><CardTitle className="text-lg">Farmer Profile</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Farmer Name *</Label><Input data-testid="farmer-name-input" value={profile.name} onChange={e => setProfile({...profile, name: e.target.value})} className="mt-1" /></div>
                  <div><Label>Farm Name *</Label><Input data-testid="farm-name-input" value={profile.farm_name} onChange={e => setProfile({...profile, farm_name: e.target.value})} className="mt-1" /></div>
                  <div className="sm:col-span-2"><Label>Location *</Label><Input data-testid="farmer-location-input" value={profile.location} onChange={e => setProfile({...profile, location: e.target.value})} className="mt-1" placeholder="City, State" /></div>
                </div>

                {/* Contact Details (Admin-only) */}
                <div className="pt-2">
                  <h4 className="text-sm font-semibold mb-2">Contact Details <span className="text-xs font-normal text-muted-foreground">(admin-only, never shown to customers)</span></h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div><Label>Phone</Label><Input data-testid="farmer-phone-input" value={profile.farmer_phone} onChange={e => setProfile({...profile, farmer_phone: e.target.value})} className="mt-1" placeholder="+91..." /></div>
                    <div><Label>WhatsApp</Label><Input data-testid="farmer-whatsapp-input" value={profile.farmer_whatsapp} onChange={e => setProfile({...profile, farmer_whatsapp: e.target.value})} className="mt-1" placeholder="+91..." /></div>
                    <div><Label>Email</Label><Input data-testid="farmer-email-input" type="email" value={profile.farmer_email} onChange={e => setProfile({...profile, farmer_email: e.target.value})} className="mt-1" placeholder="farmer@..." /></div>
                  </div>
                </div>

                <div><Label>Story</Label><Textarea data-testid="farmer-story-input" value={profile.story} onChange={e => setProfile({...profile, story: e.target.value})} className="mt-1" rows={4} /></div>
                <div><Label>Profile Image</Label><FileUpload folder="farmers/profile" onUpload={url => setProfile({...profile, profile_image: url})} existingFiles={profile.profile_image ? [profile.profile_image] : []} label="Upload profile photo" /></div>
                <div><Label>Cover Image</Label><FileUpload folder="farmers/cover" onUpload={url => setProfile({...profile, cover_image: url})} existingFiles={profile.cover_image ? [profile.cover_image] : []} label="Upload cover image" /></div>
                <div><Label>Farm Gallery (Photos & Videos)</Label><FileUpload folder="farmers/gallery" multiple onUpload={urls => setProfile({...profile, gallery: urls})} existingFiles={profile.gallery} accept="image/*,video/*" label="Upload photos and videos" /></div>
                <div><Label>Farmer Intro Video</Label><FileUpload folder="farmers/videos" type="video" onUpload={url => setProfile({...profile, intro_video: url})} existingFiles={profile.intro_video ? [profile.intro_video] : []} label="Upload intro video (MP4)" /></div>

                <div>
                  <Label>Lab Reports & Certifications (Farm-level)</Label>
                  <div className="space-y-2 mt-1">
                    {(profile.lab_reports || []).map((r, i) => (
                      <div key={i} className="flex gap-2 items-center p-2 bg-secondary/30 rounded-xl">
                        <Input placeholder="Report name (e.g. FSSAI License)" value={r.name || ''}
                          onChange={e => { const arr = [...profile.lab_reports]; arr[i] = { ...arr[i], name: e.target.value }; setProfile({ ...profile, lab_reports: arr }); }}
                          className="flex-1 text-sm" />
                        {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">View</a>}
                        <Button variant="ghost" size="icon" onClick={() => setProfile({ ...profile, lab_reports: profile.lab_reports.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                    <FileUpload folder="farmers/reports" accept=".pdf,image/*"
                      onUpload={url => {
                        if (!url) return;
                        setProfile({ ...profile, lab_reports: [...(profile.lab_reports || []), { name: `Report ${(profile.lab_reports || []).length + 1}`, url }] });
                      }}
                      existingFiles={[]} label="Upload another report (PDF/image)" />
                  </div>
                </div>

                <div>
                  <Label>Farming Practices</Label>
                  {profile.farming_practices.map((p, i) => (
                    <div key={i} className="flex gap-2 mt-1">
                      <Input value={p} onChange={e => { const arr = [...profile.farming_practices]; arr[i] = e.target.value; setProfile({...profile, farming_practices: arr}); }} placeholder="e.g. Organic Farming" />
                      {i > 0 && <Button variant="ghost" size="icon" onClick={() => setProfile({...profile, farming_practices: profile.farming_practices.filter((_, j) => j !== i)})}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" onClick={() => setProfile({...profile, farming_practices: [...profile.farming_practices, '']})} className="mt-1"><Plus className="h-3 w-3 mr-1" /> Add Practice</Button>
                </div>

                <div><Label>Internal Notes</Label><Textarea value={profile.internal_notes} onChange={e => setProfile({...profile, internal_notes: e.target.value})} className="mt-1" rows={2} placeholder="Admin-only notes" /></div>

                <Button data-testid="save-profile-button" onClick={handleSaveProfile} className="rounded-full" disabled={saving}>{saving ? 'Saving...' : 'Save & Continue'} <ArrowRight className="h-4 w-4 ml-2" /></Button>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Link to Existing Products */}
          {step === 2 && (
            <div className="space-y-6">
              <Card className="rounded-xl">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2"><Link2 className="h-4 w-4" /> Link to Products</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Products are managed in the <strong>Products module</strong>. Select which of the existing products this farmer supplies, then set their procurement price, expected harvest date, and approximate stock.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <Input data-testid="product-search-input" placeholder="Search products..." value={productSearch} onChange={e => setProductSearch(e.target.value)} className="max-w-sm" />
                    <Button variant="outline" size="sm" onClick={() => window.open('/admin/products/new', '_blank')} className="rounded-full gap-2">
                      <Plus className="h-3.5 w-3.5" /> Create new product
                    </Button>
                  </div>

                  {loadingProducts ? (
                    <p className="text-center py-10 text-sm text-muted-foreground">Loading products...</p>
                  ) : filteredProducts.length === 0 ? (
                    <div className="text-center py-10 space-y-2">
                      <Package className="h-10 w-10 text-muted-foreground mx-auto" />
                      <p className="text-sm text-muted-foreground">No products found. Create products in the Products module first.</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                      {filteredProducts.map(p => {
                        const l = links[p.product_id];
                        const isSelected = !!l?.selected;
                        return (
                          <div key={p.product_id} data-testid={`link-product-row-${p.product_id}`}
                            className={`rounded-xl border p-3 transition-colors ${isSelected ? 'border-primary bg-primary/5' : 'hover:bg-secondary/30'}`}>
                            <div className="flex items-center gap-3">
                              <Checkbox data-testid={`link-product-checkbox-${p.product_id}`} checked={isSelected} onCheckedChange={() => toggleLink(p.product_id)} />
                              {p.images?.[0] && <img src={p.images[0]} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{p.name}</p>
                                <p className="text-xs text-muted-foreground capitalize">{p.category}{p.variety && ` · ${p.variety}`} · {'\u20B9'}{p.price_per_kg}/kg</p>
                              </div>
                              <Badge variant="secondary" className="rounded-full text-xs">{p.linked_farmers_count || 0} farmer(s)</Badge>
                            </div>
                            {isSelected && (
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 pl-7">
                                <div>
                                  <Label className="text-xs">Procurement Price ({'\u20B9'}/kg)</Label>
                                  <Input data-testid={`link-procurement-${p.product_id}`} type="number" step="0.01" className="mt-1 h-8 text-sm"
                                    value={l.procurement_price} onChange={e => updateLinkField(p.product_id, 'procurement_price', e.target.value)} />
                                </div>
                                <div>
                                  <Label className="text-xs">Expected Harvest Date</Label>
                                  <Input data-testid={`link-harvest-${p.product_id}`} type="date" className="mt-1 h-8 text-sm"
                                    value={l.expected_harvest_date} onChange={e => updateLinkField(p.product_id, 'expected_harvest_date', e.target.value)} />
                                </div>
                                <div>
                                  <Label className="text-xs">Approx. Stock (kg)</Label>
                                  <Input data-testid={`link-stock-${p.product_id}`} type="number" className="mt-1 h-8 text-sm"
                                    value={l.approximate_stock_kg} onChange={e => updateLinkField(p.product_id, 'approximate_stock_kg', e.target.value)} />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)} className="rounded-full"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setStep(3)} className="rounded-full">Skip for now</Button>
                  <Button data-testid="save-links-button" onClick={handleSaveLinks} className="rounded-full" disabled={saving || selectedCount === 0}>
                    {saving ? 'Linking...' : `Link ${selectedCount} & Continue`} <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review & Publish */}
          {step === 3 && (
            <div className="space-y-6">
              <Card className="rounded-xl">
                <CardHeader><CardTitle className="text-lg">Farmer Profile</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 mb-4">
                    {profile.profile_image && <img src={profile.profile_image} alt="" className="w-16 h-16 rounded-xl object-cover" />}
                    <div>
                      <h3 className="font-medium">{profile.name}</h3>
                      <p className="text-sm text-muted-foreground">{profile.farm_name} · {profile.location}</p>
                    </div>
                  </div>
                  {(profile.farmer_phone || profile.farmer_whatsapp || profile.farmer_email) && (
                    <div className="text-xs text-muted-foreground space-y-0.5 mb-3">
                      {profile.farmer_phone && <p>Phone: {profile.farmer_phone}</p>}
                      {profile.farmer_whatsapp && <p>WhatsApp: {profile.farmer_whatsapp}</p>}
                      {profile.farmer_email && <p>Email: {profile.farmer_email}</p>}
                    </div>
                  )}
                  {profile.story && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{profile.story}</p>}
                </CardContent>
              </Card>

              <Card className="rounded-xl">
                <CardHeader><CardTitle className="text-lg">Linked Products ({selectedProducts.length})</CardTitle></CardHeader>
                <CardContent>
                  {selectedProducts.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No products linked yet. You can link products later from the farmer detail page.</p>
                  ) : (
                    selectedProducts.map(p => {
                      const l = links[p.product_id];
                      return (
                        <div key={p.product_id} className="flex items-center justify-between py-2 border-b last:border-0">
                          <div>
                            <p className="font-medium text-sm">{p.name}</p>
                            <p className="text-xs text-muted-foreground">
                              Procurement: {'\u20B9'}{l.procurement_price || 0}/kg · Stock: {l.approximate_stock_kg || 0}kg {l.expected_harvest_date && `· Harvest: ${l.expected_harvest_date}`}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)} className="rounded-full"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleSaveDraft} className="rounded-full">Save as Draft</Button>
                  <Button data-testid="publish-farmer-button" onClick={handlePublish} className="rounded-full" disabled={saving}>{saving ? 'Publishing...' : 'Publish Farmer'} <Check className="h-4 w-4 ml-2" /></Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Preview Panel */}
        {showPreview && <PreviewPanel />}
      </div>
    </div>
  );
}
